/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {ReportTranslationApiService, ReportTranslationModel} from './report-translation-api.service';
import {ReportTranslationCopyModalComponent} from './report-translation-copy-modal.component';
import {ReportTranslationEditPageComponent} from './report-translation-edit-page.component';
import {ReportTranslationRoutePageComponent} from './report-translation-route-page.component';

export function reportTranslationStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ReportTranslationApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageReportTemplates.apiKey);
  const rh = adnRouterHelper.createRouterHelper(
    'report-translations.report-translation',
    ReportTranslationApiService,
    'app.rep',
    'app'
  );
  const copyConfig = {
    modalComponent: ReportTranslationCopyModalComponent,
    stateUrl: 'app.rep.report-translations.report-translation',
    lcase: 'report translation',
    ucase: 'Report Translation'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: ReportTranslationRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      reportTranslationPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: ReportTranslationModel = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
          return new SignalAdminResourcePageState(model, true, readOnly(), api, 'Report Translation', 'name', copyConfig);
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Report Translation', 'name', copyConfig));
      },
      model: function(reportTranslationPage: SignalAdminResourcePageState<ReportTranslationModel>) {
        return reportTranslationPage.model();
      }
    },
    views: {
      'content@app': {component: ReportTranslationEditPageComponent, bindings: {state: 'reportTranslationPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'reportTranslationPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'reportTranslationPage'}}
    }
  }));
  return states.build();
}
